export interface MessageHeader {
    [key: string]: string | number | boolean | object | undefined;
}

export interface IncomingMessageHeader extends MessageHeader {
    from: string;
}

export interface RequestHeader {
    [key: string]: string | number | boolean | object | undefined;
    to: string;
    id?: number;
};

export interface ResponseHeader extends MessageHeader {
    id: number;
    to: string;
    ok: boolean;
    ask?: string;
    error?: string;
    info?: string;
    channel?: string;
}

export type NameRequestHeader = {
    name: string
    id?: number;
};

export type AskRequestHeader = {
    ask: string
    id?: number;
};

export interface IncomingMessage extends IncomingMessageHeader {
    getPayload(): string;
}

export interface IncomingResponse extends IncomingMessage {
    id: number;
    ok: boolean;
    ask?: string;
    error?: string;
    info?: string;
}

export interface IncomingRequest extends IncomingMessage {
    id: number;
}

class Signal {
    private promise: Promise<void> | null;
    private resolve: (() => void) | null;

    get signalled() {
        return this.promise === null;
    }

    constructor() {
        this.resolve = null;
        this.promise = new Promise(resolve => this.resolve = resolve);
    }

    reset() {
        if (!this.promise) {
            this.promise = new Promise(resolve => this.resolve = resolve);
        }
    }

    wait() {
        return this.promise;
    }

    signal() {
        if (this.resolve) {
            this.resolve();
            this.resolve = null;
            this.promise = null;
        }
    }
}

export class AgentRegistry {
    private socket: WebSocket | null;
    private connectionRetryTimeout: NodeJS.Timeout | null;
    private connectionRetryTimeoutValue: number;

    private currentMessageId: number;
    private registeredMessages: { [key: number]: { resolve: (result: IncomingResponse) => any, reject?: ((reason?: any) => any) | null } };
    private readySignal: Signal;

    constructor(
        private readonly url: string,
        private readonly tokenGenerator: () => Promise<string> | string,
        private readonly nameProvider: () => string
    ) {
        this.socket = null;
        this.connectionRetryTimeout = null;
        this.connectionRetryTimeoutValue = 1000;

        this.currentMessageId = 0;
        this.registeredMessages = {};
        this.readySignal = new Signal();

        this.connect();
    }

    waitUntilReady() {
        return this.readySignal.wait();
    }

    isReady() {
        return this.readySignal.signalled;
    }

    isConnected() {
        return this.socket?.readyState === WebSocket.OPEN;
    }

    close() {
        if (this.connectionRetryTimeout) {
            clearTimeout(this.connectionRetryTimeout);
        }

        if (this.socket && (this.isConnected() || this.socket.readyState === WebSocket.CONNECTING)) {
            this.socket.close();
        }
    }

    send(header: MessageHeader, payload?: string | null) {
        if (!this.socket) {
            console.warn("Trying to send message before socket was created");
            return;
        }

        const headerData = JSON.stringify(header);

        if (headerData.length > 1024) {
            throw new Error("Header too long");
        }

        this.socket.send(headerData + "\n" + (payload ?? ""));
    }

    request(header: NameRequestHeader): Promise<IncomingResponse>;
    request(header: AskRequestHeader): Promise<IncomingResponse>;
    request(header: RequestHeader, payload?: string): Promise<IncomingResponse>;
    request(header: RequestHeader | NameRequestHeader | AskRequestHeader, payload?: string): Promise<IncomingResponse> {
        const currentId = this.currentMessageId;
        this.currentMessageId++;

        header.id = currentId;

        this.registeredMessages[currentId] = { resolve: null!, reject: null };

        this.send(header, payload);

        const timeout = setTimeout(() => {
            const reject = this.registeredMessages[currentId].reject;

            if (reject) {
                reject(new Error("Request has timed out"));
            }
        }, 2000);

        return new Promise<IncomingResponse>((resolve, reject) => {
            this.registeredMessages[currentId].resolve = (arg?: any) => {
                clearTimeout(timeout);
                resolve(arg);
            };
            this.registeredMessages[currentId].reject = reject;
        });
    }

    respond(message: IncomingRequest, payload?: string | null, info?: string | null, error?: string | null, channel?: string) {
        const header: ResponseHeader = {
            id: message.id,
            to: message.from,
            info: info === null ? undefined : info,
            ok: error === undefined || error === null,
            error: error === null ? undefined : error,
            channel
        };

        this.send(header, payload);
    }

    protected onMessage(msg: IncomingMessage) {

    }

    protected onRequest(msg: IncomingRequest) {

    }

    private async connect() {
        const searchParams = new URLSearchParams();
        const tokenResult = this.tokenGenerator();
        const token = typeof tokenResult === "string" ? tokenResult : await tokenResult;

        if (!token) {
            this.connectionRetryTimeout = setTimeout(() => {
                console.log("WebSocket connection retrying");
                this.connectionRetryTimeout = null;
                this.connect();
            }, this.connectionRetryTimeoutValue);

            if (this.connectionRetryTimeoutValue < 10000) {
                this.connectionRetryTimeoutValue += 1000;
            }

            return;
        }

        searchParams.set("token", token);

        console.log("WebSocket connecting...");
        this.socket = new WebSocket(this.url + "?" + searchParams.toString());

        this.socket.onopen = () => {
            console.log("WebSocket connection opened");
            this.connectionRetryTimeout = null;
            this.onConnected();
        }

        this.socket.onclose = () => {
            console.log("WebSocket connection closing");
            this.readySignal.reset();

            console.log("WebSocket connection retrying after " + (this.connectionRetryTimeoutValue / 1000) + " seconds");
            this.connectionRetryTimeout = setTimeout(() => {
                console.log("WebSocket connection retrying");
                this.connectionRetryTimeout = null;
                this.connect();
            }, this.connectionRetryTimeoutValue);

            if (this.connectionRetryTimeoutValue < 10000) {
                this.connectionRetryTimeoutValue += 1000;
            }
        };

        this.socket.onerror = (e) => {
            console.log("WebSocket connection error: " + e.toString());
        };

        this.socket.onmessage = (e) => {
            if (typeof e.data === "string") {
                this.onRawMessage(e.data)
            }
        };
    }

    private async onConnected() {
        let tries = 0;

        while(this.isConnected()) {
            const response = await this.request({
                name: this.nameProvider()
            });

            if (response.error) {
                tries++;
            }
            else if (response.info) {
                break;
            }

            if (tries > 10) {
                console.warn("Cannot set registry name: " + response.error + " - closing the webscoket");
                this.close();
                return;
            }
        }

        this.connectionRetryTimeoutValue = 1000;
        this.readySignal.signal();
    }

    private onRawMessage(msg: string) {
        let headerLength = this.extractHeaderLength(msg);

        if (headerLength === -1) {
            console.error("Received a message with header that exceeds 1kB in size");
            return;
        }

        let message: IncomingMessage;

        try {
            message = JSON.parse(msg.substring(0, headerLength));
        }
        catch(ex) {
            console.error("Received a message with header in invalid format");
            return;
        }

        if (typeof message.from !== "string") {
            console.error("Received a message without 'from' value identifying a sender");
            return;
        }

        let payload: string | null = null;
        message.getPayload = function() {
            if (payload === null) {
                payload = msg.substring(headerLength + 1);
            }

            return payload;
        };

        // If the message contains id property then it is a request or response
        if (typeof message.id === "number") {
            // If the message contains ok property then it is a response
            if (typeof message.ok === "boolean") {
                if (message.id === -1) {
                    console.error("Global error: " + message.error);
                    return;
                }

                if (!this.registeredMessages[message.id]) {
                    console.error("Received message with ID that was not registered");
                    return;
                }

                const registeredMessage = this.registeredMessages[message.id];
                delete this.registeredMessages[message.id];
                registeredMessage.resolve(message as IncomingResponse);
                return;
            }

            if (message.id < 0) {
                console.error(`Received request with invalid id: ${message.id}`);
                return;
            }

            // Otherwise the message is a request
            this.onRequest(message as IncomingRequest);
        }
        else {
            this.onMessage(message);
        }
    }

    private extractHeaderLength(msg: string) {
        let separatorIndex = msg.lastIndexOf("\n", 1025);

        if (separatorIndex === -1 && msg.length <= 1024) {
            separatorIndex = msg.length;
        }
        else if (separatorIndex !== -1) {
            separatorIndex = msg.indexOf("\n");
        }

        return separatorIndex;
    }
}
